import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {
  IConversationTypeInterfacesMap,
} from '@toolify/server/src/services/MessageService/types/IConversationTypeInterfacesMap'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {RouteLocationRaw} from 'vue-router'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {IResponseResourcePath} from '@toolify/server/src/services/ResourceService/types/resourcePath/IResponseResourcePath'
import {findResourceByResourceTypeInPath} from '../../activity/singletons/findResourceByResourceTypeInPath'
import {LogTitleItem} from '../../activity/types/ILogTitleItem'
import {LogItemTitleItemType} from '../../activity/enum/LogItemTitleItemType'
import {i18n} from '@toolify/client/src/setup/i18n'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {getEntityNameByEntity} from '../../entities/functions/getEntityNameByEntity'

import {
  IWorkspaceResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IWorkspaceResponseInstance'
import {
  buildInlineNotificationResourceTitleItems,
} from '@toolify/client/src/modules/notifications/functions/buildInlineNotificationResourceTitleItems'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'

const {t} = i18n.global

export type INotificationResourceDetailsOptions = {
  /** NewMessage strings already end with "in"/"w". */
  omitInPrefix?: boolean
  /** Used as a display fallback when the conversation itself has no name. */
  authorEntity?: IResponseEntity
}

type NotificationResourceDetailsFn = (
  notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'],
  resourcePath: IResponseResourcePath,
  options?: INotificationResourceDetailsOptions,
) => {
  resourceName: string
  route: RouteLocationRaw
  titleItems: LogTitleItem[]
}

export const NotificationResourceDetailsByConversationTypeMap: Record<ConversationType, NotificationResourceDetailsFn> = {
  [ConversationType.Workspace]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], resourcePath: IResponseResourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.Workspace]['notificationPayload']
    const resourceText = typedPayload.workspaceConversationName || t('general.noTitle')
    return {
      resourceName: resourceText,
      route: {
        name: RouteName.Conversation,
        params: {
          conversationId: typedPayload.workspaceConversationId,
          workspaceName: typedPayload.workspaceName,
        },
        query: {
          stackable: 'true',
        },
      },
      titleItems: buildInlineNotificationResourceTitleItems({
        text: resourceText,
        iconNameType: IconNameType.Comments,
      }, {
        omitInPrefix: options?.omitInPrefix,
      }),
    }
  },
  [ConversationType.DatabaseCell]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], resourcePath: IResponseResourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.DatabaseCell]['notificationPayload']
    const database = findResourceByResourceTypeInPath(resourcePath, ResourceType.Database)
    const databaseItem = findResourceByResourceTypeInPath(resourcePath, ResourceType.DatabaseItem)
    const workspace = resourcePath.find(r => r.resourceType === ResourceType.Workspace)
    const workspaceName = typedPayload.workspaceName ?? (workspace?.resource as IWorkspaceResponseInstance)?.name ?? workspace?.resourceId

    const route = workspaceName ? {
      name: RouteName.DatabaseItem,
      params: {
        workspaceName,
        databaseId: typedPayload.databaseId,
        databaseItemId: typedPayload.databaseItemId,
      },
      query: {
        shouldCommentsBeOpened: 'true',
      },
    } : null

    const databaseTitle = database ? database.title || t('general.noTitle') : t('notifications.notificationItemTitleItems.resourceRemoved')
    return {
      resourceName: databaseTitle,
      route,
      titleItems: [
        {
          type: LogItemTitleItemType.Item,
          payload: {
            text: databaseItem ? `${databaseItem.name || t('general.noTitle')}` : t('notifications.notificationItemTitleItems.itemRemoved'),
            iconNameType: database?.iconNameType,
          },
        },
        // Item above is the conversation target ("messaged in {item}"); footer was the database.
        ...buildInlineNotificationResourceTitleItems({
          text: databaseTitle,
          iconNameType: database?.iconNameType,
        }),
      ],
    }
  },
  [ConversationType.DatabaseFile]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], resourcePath: IResponseResourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.DatabaseFile]['notificationPayload']
    const databaseItem = findResourceByResourceTypeInPath(resourcePath, ResourceType.DatabaseItem)
    const workspace = findResourceByResourceTypeInPath(resourcePath, ResourceType.Workspace)
    const workspaceName = (workspace as { name?: string; resource?: { name?: string } })?.name ?? (workspace as { resource?: { name?: string } })?.resource?.name
    const resourceText = databaseItem ? databaseItem.name || t('general.noTitle') : t('notifications.notificationItemTitleItems.resourceRemoved')
    return {
      resourceName: resourceText,
      route: {
        name: RouteName.DatabaseItemFile,
        params: {
          workspaceName,
          databaseId: typedPayload.databaseId,
          databaseItemId: typedPayload.databaseItemId,
          databaseItemFileId: typedPayload.databaseItemFileId,
        },
        query: {
          shouldCommentsBeOpened: 'true',
        },
      },
      titleItems: buildInlineNotificationResourceTitleItems({
        text: resourceText,
        iconNameType: IconNameType.File,
      }, {
        omitInPrefix: options?.omitInPrefix,
      }),
    }
  },
  [ConversationType.CloudFile]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], resourcePath: IResponseResourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.CloudFile]['notificationPayload']
    const workspace = findResourceByResourceTypeInPath(resourcePath, ResourceType.Workspace)
    const workspaceName = (workspace as { name?: string; resource?: { name?: string } })?.name ?? (workspace as { resource?: { name?: string } })?.resource?.name
    return {
      resourceName: typedPayload.cloudItemName,
      route: {
        name: RouteName.CloudFile,
        params: {
          workspaceName,
          fileCloudItemId: typedPayload.cloudItemId,
        },
      },
      titleItems: buildInlineNotificationResourceTitleItems({
        text: typedPayload.cloudItemName || t('general.noTitle'),
        iconNameType: IconNameType.Folder,
      }, {
        omitInPrefix: options?.omitInPrefix,
      }),
    }
  },
  [ConversationType.Direct]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], resourcePath: IResponseResourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.Direct]['notificationPayload']
    const authorName = options?.authorEntity ? getEntityNameByEntity(options.authorEntity) : null
    const route: RouteLocationRaw = {
      name: RouteName.DirectConversation,
      params: {
        directConversationId: typedPayload.directConversationId,
      },
      query: {
        stackable: 'true',
      },
    }
    return {
      resourceName: typedPayload.directConversationName || authorName || t('general.noTitle'),
      route,
      titleItems: [],
    }
  },
  [ConversationType.CallRoom]: (notificationPayload: IConversationTypeInterfacesMap[ConversationType]['notificationPayload'], _resourcePath, options) => {
    const typedPayload = notificationPayload as IConversationTypeInterfacesMap[ConversationType.CallRoom]['notificationPayload']
    const resourceText = typedPayload.callRoomName || t('general.noTitle')
    return {
      resourceName: resourceText,
      route: {
        name: RouteName.CallRoom,
        params: {
          callRoomId: typedPayload.callRoomId,
        },
      },
      titleItems: buildInlineNotificationResourceTitleItems({
        text: resourceText,
        iconNameType: IconNameType.Phone,
      }, {
        omitInPrefix: options?.omitInPrefix,
      }),
    }
  },
}
